
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import Component from 'vue-class-component';
import moment, {MomentInput} from 'moment';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import TableCollapseToggle from '@/components/TableCollapseToggle.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

interface StockCard {
    itemCode: string,
    itemDesc: string,
}

interface Totals {
    sub_total: number,
    total_discount: number,
    grand_total: number,
}

@Component({
    components: {
        TableCollapseToggle,
        SelectFilter,
        TableFilterable,
    },
})
export default class TiarcoChemicalMOtherIncomeBreakdownTable extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'month'
        | 'dateRange'
        | 'stockItems'
        | 'itemCode'
    > = ['dateRange', 'month', 'stockItems', 'itemCode'];

    public tableItems: object[] = [];
    public tableFields: object[] = [];
    public periodTotalFields: any[] = [
        {
            key: 'sub_total',
            formatter: TableItemFormatter.currencySplit,
            textAlign: 'center',
        },
        {
            key: 'total_discount',
            formatter: TableItemFormatter.currencySplit,
            textAlign: 'center',
        },
        {
            key: 'grand_total',
            formatter: TableItemFormatter.currencySplit,
            textAlign: 'center',
        },
    ];
    public periodTotalData: Totals[] = [];
    public itemCode: string = 'Others';
    public stockCards: StockCard[] = [];
    public options: any[] | undefined = [];

    public exportName(range: number[]) {
        return 'NYCX Discount By Item Code' + ' from ' +
            moment(range[0] as MomentInput).format('DD MMM YY') +
            ' - ' +
            moment(range[1] as MomentInput).format('DD MMM YY');
    }

    public get generateTitleText() {
        const stockCard = this.stockCards.find((card) => card.itemCode === this.itemCode);
        const itemDesc = stockCard ? stockCard.itemDesc : null;
        return `Discount Table for [${itemDesc}]`;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            itemCode,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            itemCode,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.4);

        const queryHead = await this._ensureTablesLoaded(
            ['sales', 'globalStocks'] as any[],
        );
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.4, 0.7);

        const salesTable = getTableName('sales');
        const stocksTable = getTableName('globalStocks');

        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
        const itemFilter = ` AND ${COL.ITEMCODE} = '${escapeStr(this.itemCode)}'`;

        const [salesRows, stockRows] = await Promise.all([
            queryHead.rawQuery(`
                SELECT ${COL.DOCDATE} as docDate, ${COL.DOCNO} as docNo,
                    TRY_CAST(${COL.QUANTITY} AS DOUBLE) as qty,
                    TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amt,
                    TRY_CAST(${COL.DISCOUNT} AS DOUBLE) as disc
                FROM ${salesTable}
                WHERE ${dateFilter}${itemFilter}
            `),
            queryHead.rawQuery(`
                SELECT "CODE" as itemCode, "DESCRIPTION" as itemDesc
                FROM ${stocksTable}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.7, 0.9);

        this.stockCards = stockRows.map((r: any) => ({
            itemCode: String(r.itemCode || ''),
            itemDesc: String(r.itemDesc || ''),
        }));

        this.options = this.stockCards.map((card) => ({
            text: card.itemCode,
            value: card.itemCode,
        }));

        const newArray: any = [];

        for (const row of salesRows) {
            const qty = Number(row.qty) || 0;
            const amt = Number(row.amt) || 0;
            const disc = Number(row.disc) || 0;
            newArray.push({
                date: moment(Number(row.docDate)).format('DD/MM/YY'),
                doc_no: String(row.docNo || ''),
                qty,
                unit_price: amt / (qty || 1),
                sub_total: Math.round(amt),
                disc: Math.round(disc),
                total: Math.round(amt - disc),
            });
        }

        this.tableItems = newArray;

        this.periodTotalData = [
            {
                sub_total: newArray.reduce((a: any, b: { sub_total: any; }) => a + b.sub_total, 0),
                total_discount: newArray.reduce((a: any, b: { disc: any; }) => a + b.disc, 0),
                grand_total: newArray.reduce((a: any, b: { total: any; }) => a + b.total, 0),
            },
        ];

        this.tableFields = [
            {
                key: 'date',
                sortable: false,
            },
            {
                key: 'doc_no',
                sortable: false,
            },
            {
                key: 'qty',
                sortable: false,
            },
            {
                key: 'sub_total',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'disc',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'total',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },

        ];

        this.saveHistory('stockCards', 'options', 'tableItems', 'periodTotalData', 'tableFields');
        this.generateLoadingText(1);
    }


}
